export type GridSwitchOption = {
	value: string;
	title: string;
};

export type DatedSubmission = {
	id: string;
	localDate: string;
	createdAt: string;
	templateID: string;
	title: string;
};

const day = (value: string) => value.slice(0, 10);

const byTitle = (a: GridSwitchOption, b: GridSwitchOption) => a.title.localeCompare(b.title);

/*
 * Templates the manager can switch to while filling a grid: every non-archived
 * template, plus the current one when it is archived and still open.
 */
export function liveGridSwitchOptions(
	current: { id: string; title: string },
	templates: { id: string; title: string }[],
): GridSwitchOption[] {
	const titles = new Map<string, string>();
	for (const template of templates) {
		titles.set(template.id, template.title);
	}
	if (!titles.has(current.id)) {
		titles.set(current.id, current.title);
	}

	return [...titles.entries()].map(([value, title]) => ({ value, title })).sort(byTitle);
}

/*
 * On a submitted grid, only templates with an active submission on that day.
 * The current template stays in the list so it can show as selected. When a
 * template has more than one active submission, the newest is the target.
 */
export function submittedGridSwitch(
	current: { templateID: string; title: string; snapshotID: string },
	localDate: string,
	submissions: DatedSubmission[],
): {
	options: GridSwitchOption[];
	snapshotIDByTemplateID: Map<string, string>;
} {
	const newest = new Map<string, DatedSubmission>();

	for (const submission of submissions) {
		if (day(submission.localDate) !== day(localDate)) {
			continue;
		}

		const existing = newest.get(submission.templateID);
		if (!existing || submission.createdAt > existing.createdAt) {
			newest.set(submission.templateID, submission);
		}
	}

	if (!newest.has(current.templateID)) {
		newest.set(current.templateID, {
			id: current.snapshotID,
			localDate,
			createdAt: '',
			templateID: current.templateID,
			title: current.title,
		});
	}

	const snapshotIDByTemplateID = new Map<string, string>();
	const options: GridSwitchOption[] = [];

	for (const submission of newest.values()) {
		snapshotIDByTemplateID.set(submission.templateID, submission.id);
		options.push({
			value: submission.templateID,
			title:
				submission.title || (submission.templateID === current.templateID ? current.title : ''),
		});
	}

	options.sort(byTitle);

	return { options, snapshotIDByTemplateID };
}
